import { useCallback, useEffect, useState } from 'react';

import { UnifiedIcon } from '@/components/UnifiedIcon';

import {
  isValidEditorLinkHref,
  normalizeEditorLinkUrl,
} from './core/normalizeEditorLinkUrl.js';

/**
 * @param {string} text
 * @returns {string}
 */
function escapeHtmlText(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {import('@tiptap/core').Editor} editor
 * @param {string} urlInput
 * @returns {{ ok: boolean, errorKey?: string }}
 */
function applyEditorLink(editor, urlInput) {
  const trimmed = urlInput.trim();

  if (!trimmed) {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return { ok: true };
  }

  const normalized = normalizeEditorLinkUrl(trimmed);

  if (!normalized || !isValidEditorLinkHref(normalized)) {
    return { ok: false, errorKey: 'editor.link.invalidUrl' };
  }

  const { empty } = editor.state.selection;

  if (empty) {
    const display = escapeHtmlText(trimmed.replace(/^https?:\/\//i, '') || normalized);
    const safeHref = escapeHtmlText(normalized);
    editor
      .chain()
      .focus()
      .insertContent(`<a href="${safeHref}">${display}</a>`)
      .run();
  } else {
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
  }

  return { ok: true };
}

/**
 * @param {import('./EditorLinkPopover').EditorLinkPopoverProps} props
 * @returns {JSX.Element}
 */
export function EditorLinkPopover({
  editor,
  isOpen,
  onClose,
  t,
}) {
  const [urlInput, setUrlInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isOpen || !editor) {
      return;
    }

    const existingHref = editor.getAttributes('link').href;
    setUrlInput(typeof existingHref === 'string' ? existingHref : '');
    setErrorMessage('');
  }, [editor, isOpen]);

  const handleApply = useCallback(() => {
    if (!editor) {
      return;
    }

    const result = applyEditorLink(editor, urlInput);

    if (!result.ok) {
      setErrorMessage(t(result.errorKey ?? 'editor.link.invalidUrl'));
      return;
    }

    setErrorMessage('');
    onClose();
  }, [editor, onClose, t, urlInput]);

  const handleUnlink = useCallback(() => {
    if (!editor) {
      return;
    }

    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setUrlInput('');
    setErrorMessage('');
    onClose();
  }, [editor, onClose]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleApply();
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    },
    [handleApply, onClose]
  );

  if (!isOpen || !editor) {
    return null;
  }

  const isLinkActive = editor.isActive('link');
  const applyLabel = t('editor.link.apply');
  const unlinkLabel = t('editor.toolbar.unlink');
  const cancelLabel = t('editor.link.cancel');
  const urlLabel = t('editor.link.urlLabel');
  const placeholder = t('editor.link.urlPlaceholder');

  return (
    <div
      className="absolute left-0 top-11 z-30 w-80 rounded-xl border border-gray-200 bg-white p-3 shadow-lg shadow-gray-200/80 dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30"
      data-testid="widgets-editor-link-popover"
    >
      <label
        className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300"
        htmlFor="widgets-editor-link-popover-url"
      >
        {urlLabel}
      </label>
      <input
        autoFocus
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-violet-500 focus:border-violet-500 focus:ring-1 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
        data-testid="widgets-editor-link-popover-url"
        id="widgets-editor-link-popover-url"
        onChange={(event) => {
          setUrlInput(event.target.value);
          if (errorMessage) {
            setErrorMessage('');
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        type="url"
        value={urlInput}
      />
      {errorMessage ? (
        <p
          className="mt-2 text-xs text-red-600 dark:text-red-400"
          data-testid="widgets-editor-link-popover-error"
          role="alert"
        >
          {errorMessage}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          className="inline-flex items-center justify-center rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-600"
          data-testid="widgets-editor-link-popover-apply"
          onMouseDown={(event) => {
            event.preventDefault();
            handleApply();
          }}
          type="button"
        >
          {applyLabel}
        </button>
        <button
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          data-testid="widgets-editor-link-popover-cancel"
          onMouseDown={(event) => {
            event.preventDefault();
            onClose();
          }}
          type="button"
        >
          {cancelLabel}
        </button>
        {isLinkActive ? (
          <button
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            data-testid="widgets-editor-link-popover-unlink"
            onMouseDown={(event) => {
              event.preventDefault();
              handleUnlink();
            }}
            type="button"
          >
            <UnifiedIcon className="h-4 w-4" library="phosphor" name="LinkBreak" size={16} />
            {unlinkLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
